'use client'

import { ChevronDown } from '@carbon/icons-react'

import { cn } from '@/lib/utils'

import {
  EASE,
  HAIRLINE,
  InlineLink,
  SectionHead,
  useReveal,
} from '@/components/page-kit'
import { faqPageJsonLd, type ProductFaqItem } from './faqs'

/* The FAQ block shared by the five bespoke product pages. Same grammar as the
   rest of them — hairline rules, an ordinal in the margin, the question set as
   display type on its own row — and the same disclosure the school ERP FAQ
   uses, so the two read as one site.

   Built on <details>/<summary> rather than the Radix accordion in
   components/ui: Radix unmounts a closed panel, so the answers would exist only
   after a click. This section emits the FAQPage JSON-LD for its own questions,
   and that markup is only legal if the page really renders the text — <details>
   keeps every answer in the delivered HTML while still shipping it collapsed.
   Same contract as <Breadcrumbs>: one array, rendered visibly and as markup, so
   the two cannot disagree. */

export function ProductFaq({
  path,
  eyebrow = 'Questions from buyers',
  title,
  deck,
  items,
}: {
  /** Route this section lives on, e.g. `/products/lms`. Anchors the JSON-LD. */
  path: string
  eyebrow?: string
  title: string
  deck?: string
  items: readonly ProductFaqItem[]
}) {
  const list = useReveal<HTMLDivElement>(0.08)

  return (
    <section id="faq" className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqPageJsonLd(items, path)),
        }}
      />

      <div className="container mx-auto">
        <SectionHead eyebrow={eyebrow} title={title} deck={deck} />

        <div ref={list.ref} className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}>
          {items.map((f, i) => (
            <details
              key={f.question}
              className={cn('group border-b', HAIRLINE)}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(8px)',
                transition: `opacity 480ms ${EASE} ${i * 60}ms, transform 480ms ${EASE} ${i * 60}ms`,
              }}
            >
              <summary
                className={cn(
                  'flex cursor-pointer list-none items-baseline gap-3 py-5 sm:py-6',
                  '[&::-webkit-details-marker]:hidden',
                  'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4',
                  'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active'
                )}
              >
                <span className="shrink-0 text-[11px] font-semibold tabular-nums text-muted-foreground/60">
                  {String(i + 1).padStart(2, '0')}
                </span>

                <h3 className="font-heading min-w-0 flex-1 text-base font-normal leading-snug text-foreground sm:text-lg lg:text-xl">
                  {f.question}
                </h3>

                <ChevronDown
                  aria-hidden
                  size={20}
                  className={cn(
                    'shrink-0 self-start text-muted-foreground transition-transform duration-200',
                    'group-open:rotate-180 group-open:text-emerald-600 dark:group-open:text-primary-active'
                  )}
                />
              </summary>

              <div className="pb-6 pl-[26px]">
                <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {f.answer}
                </p>

                {f.links && f.links.length > 0 && (
                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                    <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground/70">
                      See also
                    </span>{' '}
                    {f.links.map((l, n) => (
                      <span key={l.href}>
                        {n > 0 && <span aria-hidden> · </span>}
                        <InlineLink href={l.href}>{l.label}</InlineLink>
                      </span>
                    ))}
                  </p>
                )}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}
